import { useCallback, useEffect, useRef, useState } from 'react';
import { appApiUrl } from '../../../utils/appApiOrigin';

const POLL_MS = 15_000;

export function useWebStreamMetadata(streamUrl, { enabled = true } = {}) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    const url = String(streamUrl || '').trim();
    if (!url || !enabled) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        appApiUrl(`/api/public/stream-metadata?url=${encodeURIComponent(url)}`),
        { credentials: 'include' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || 'Could not load stream info');
      }
      setMeta(data.stream || null);
    } catch (e) {
      setError(e.message || 'Could not load stream info');
    } finally {
      setLoading(false);
    }
  }, [streamUrl, enabled]);

  useEffect(() => {
    setMeta(null);
    setError('');
  }, [streamUrl]);

  useEffect(() => {
    if (!enabled || !streamUrl) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return undefined;
    }
    refresh();
    timerRef.current = window.setInterval(refresh, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled, streamUrl, refresh]);

  return { meta, loading, error, refresh };
}
